import React, { useState } from 'react';
import { Home, Mail, Lock, ArrowRight } from 'lucide-react';
import logo from '../assets/logo.png';

const Login = ({ onNavigate, onLoginSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleLogin = (e) => {
    e.preventDefault();
    setLoading(true);

    setTimeout(() => {
      setLoading(false);

      // Check for blocked users (simulated logic)
      const blockedUsers = JSON.parse(localStorage.getItem('blockedUsers') || '[]');

      if (blockedUsers.includes(formData.email)) {
        alert('Access Denied: Your account has been blocked by the Administrator.');
        return;
      }

      // Admin Login Check
      // Ideally, we check password too. For demo:
      if (formData.email === 'admin@agriscan.ai') {
        if (formData.password === 'admin123') { // Simple password check for admin redirect flow
          onNavigate && onNavigate('admin');
        } else {
          alert('Invalid Admin Password (Try: admin123)');
        }
      } else {
        // Regular user login simulation
        // Find if user exists in our mock list (just for demo purposes, we'll pretend they are "Ramesh Kumar" if not admin)
        // In reality, you'd fetch user from backend.
        // For this verified tick demo, we will assign a mock identity based on email or default to one.

        const mockUser = {
          name: "Ramesh Kumar",
          email: formData.email || "ramesh@example.com",
          role: "Farmer"
        };

        if (onLoginSuccess) onLoginSuccess(mockUser);
        onNavigate && onNavigate('home');
      }
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-transparent flex items-center justify-center px-4 py-12">
      <div className="max-w-4xl w-full bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row">

        {/* Left Side - Visual */}
        <div className="hidden md:flex md:w-1/2 bg-gradient-to-br from-emerald-600 to-teal-700 text-white p-12 flex-col justify-between relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1625246333195-58197bd47f26?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10 mix-blend-overlay"></div>
          <div className="relative z-10">
            <div className="flex items-center space-x-3 mb-8">
              <img src={logo} alt="Logo" className="w-10 h-10 rounded-lg shadow-lg" />
              <span className="text-xl font-bold tracking-tight">AgriScan AI</span>
            </div>
            <h2 className="text-4xl font-black mb-6 leading-tight">Welcome Back,<br />Farmer!</h2>
            <p className="text-emerald-100 text-lg">Access your dashboard, check specific crop insights, and connect with the community.</p>
          </div>

          <div className="relative z-10 mt-12">
            <div className="flex -space-x-4 mb-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="w-10 h-10 rounded-full border-2 border-emerald-600 bg-emerald-800"></div>
              ))}
            </div>
            <p className="text-sm text-emerald-200">Join 10,000+ farmers using AgriScan</p>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="w-full md:w-1/2 p-8 sm:p-12 bg-white">
          <div className="text-right mb-8">
            <button onClick={() => onNavigate && onNavigate('home')} className="text-slate-400 hover:text-emerald-600 transition-colors flex items-center justify-end ml-auto">
              <Home className="w-4 h-4 mr-1" /> Home
            </button>
          </div>

          <div className="mb-10">
            <h3 className="text-2xl font-bold text-slate-800 mb-2">Sign in to Account</h3>
            <p className="text-slate-500">Enter your email and password to continue.</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            <button type="button" className="w-full flex items-center justify-center space-x-3 py-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors mb-6">
              {/* Google Logo SVG */}
              <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" /><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" /><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" /><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" /></svg>
              <span className="font-semibold text-slate-700">Continue with Google</span>
            </button>

            <div className="relative text-center mb-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-100"></div></div>
              <div className="relative inline-block bg-white px-2 text-xs text-slate-400">OR</div>
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">Email Address</label>
              <div className="relative">
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="you@example.com"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 pl-11 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all font-medium text-slate-800"
                />
                <Mail className="w-5 h-5 text-slate-400 absolute left-3.5 top-3.5" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">Password</label>
              <div className="relative">
                <input
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 pl-11 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all font-medium text-slate-800"
                />
                <Lock className="w-5 h-5 text-slate-400 absolute left-3.5 top-3.5" />
              </div>
              <div className="text-right mt-2">
                <button type="button" className="text-xs font-semibold text-emerald-600 hover:text-emerald-700">Forgot Password?</button>
              </div>
            </div>

            <button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3.5 rounded-xl font-bold shadow-lg shadow-emerald-200 transition-all hover:scale-[1.02] active:scale-95 flex items-center justify-center">
              {loading ? 'Signing In...' : 'Sign In'} <ArrowRight className="w-5 h-5 ml-2" />
            </button>
          </form>

          <div className="text-center mt-8">
            <p className="text-slate-500 text-sm">
              Don't have an account? {' '}
              <button onClick={() => onNavigate && onNavigate('signup')} className="text-emerald-600 font-bold hover:underline">Create Account</button>
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Login;
